import React from 'react';
import { NavLink, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './MobileTabBar.scss';
import { show as showIntercom } from '@intercom/messenger-js-sdk';
import { GEO_REPORT, INTEGRATIONS, PROJECTS } from '../../../routes/path';
import { useAccount } from '../../../contexts/account/AccountContexts';
import useDisabledAccount from '../../../hooks/account/UseDisabledAccount';
import { REACT_APP_INTERCOM_ENABLED, REACT_APP_INTERCOM_MESSENGER_ENABLED } from '../../../utils/config';
import { buildProjectMenu } from '../panel/helpers/ProjectMenuHelper';

// Slot for a page's own items (`MobileTabBarItems`): when it is filled, the page's items REPLACE the
// default ones — the bar has room for four, and a page that needs its own set (the admin account page)
// knows better than the defaults what matters there.
export const MOBILE_TAB_BAR_PAGE_ID = 'mobile-tab-bar-page';

interface TabItem {
  to: string;
  icon: string;
  label: string;
  /** Prefiks adresu, po którym zakładka jest aktywna (gdy `to` ma na końcu okres/model). */
  match?: string;
  /** Aktywna tylko na dokładnie tym adresie (lista projektów, a nie strona projektu). */
  end?: boolean;
}

// Pozycje menu projektu, które trafiają do paska, i ich krótsze etykiety (pełne nie mieszczą się
// pod ikoną). Kolejność = kolejność w pasku.
const PROJECT_TABS: { id: string; labelKey?: string }[] = [
  { id: 'overview-btn' },
  { id: 'sources-btn' },
];

/**
 * Dolny pasek zakładek na telefonie, zależny od miejsca:
 * - poza projektem: Projekty, GEO Audit, Integracje, Pomoc (raporty są per projekt — w menu projektu);
 * - w projekcie: Projekty (powrót do listy), Przegląd, Źródła, Pomoc — reszta sekcji projektu jest
 *   w menu pod hamburgerem.
 * Najwyżej cztery pozycje.
 * Pozycje znikają na tych samych warunkach co w panelu: sekcje projektu bierzemy wprost z
 * `buildProjectMenu`, więc nie mają tu własnych reguł. „Pomoc” otwiera czat Intercom zamiast pływającej
 * ikonki, która na telefonie zasłaniałaby pasek (chowana w `MobileTabBar.scss`).
 */
const MobileTabBar: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { accountResponse, projectResponse } = useAccount();
  const { isDisabled } = useDisabledAccount();
  // Wszystkie trasy projektu mają `:projectId`. Projekt z listy konta, jak w `Panel` — odpowiedź
  // `GET /project/:id` nie ma `defaultPeriod`, bez którego `buildProjectMenu` nie zbuduje adresów.
  const { projectId } = useParams();
  const project = projectResponse?.find((p) => p.id === Number(projectId));

  if (!accountResponse) {
    return null;
  }

  const addons = accountResponse.addons;
  const isGuest = accountResponse.role === 'GUEST';

  // Sekcje projektu z tego samego źródła co panel. Pozostałe pola kontekstu dotyczą wyłącznie pozycji,
  // których w pasku nie ma (raporty, insights, boty), więc ich wartość nie wpływa na wynik.
  const projectEntries =
    project && !isDisabled
      ? buildProjectMenu({
          project,
          addons,
          showVisibilityReport: false,
          isVisibilityReportDevOnly: false,
          hasAdsAddon: false,
          insights: { canAccess: false, isDevOnly: false },
          promptAnalyst: { canAccess: false },
          botsActivityHasAnyDataSource: false,
          isGuest,
        }).flatMap((entry) => (entry.kind === 'item' ? [entry.item] : entry.group.items))
      : [];
  const projectItems: TabItem[] = PROJECT_TABS.flatMap(({ id, labelKey }) => {
    const item = projectEntries.find((entry) => entry.id === id);
    return item
      ? [{ to: item.to, icon: item.icon, label: t(labelKey ?? item.labelKey), match: item.match }]
      : [];
  });

  const items: TabItem[] =
    projectItems.length > 0
      ? [{ to: PROJECTS, icon: 'ico--folder', label: t('projects'), end: true }, ...projectItems]
      : [
          ...(!isDisabled ? [{ to: PROJECTS, icon: 'ico--folder', label: t('projects') }] : []),
          ...(!isGuest && !isDisabled && addons.includes('GEO_REPORT')
            ? [{ to: GEO_REPORT, icon: 'ico--report', label: t('geoReport.navLabel') }]
            : []),
          ...(!isGuest && (addons.includes('LOOKER_STUDIO') || addons.includes('AI_SIGNALS'))
            ? [{ to: INTEGRATIONS, icon: 'ico--puzzle', label: t('integrations') }]
            : []),
        ];
  // Te same warunki, na których `IntercomChat` uruchamia czat z widoczną ikonką. Pasek ma najwyżej cztery
  // pozycje — „Pomoc” wchodzi tylko, gdy zostało miejsce.
  const hasChat =
    REACT_APP_INTERCOM_ENABLED && REACT_APP_INTERCOM_MESSENGER_ENABLED && !isGuest && items.length < 4;

  // Pasek z jedną pozycją (np. gość: same Projekty) nie ma czego przełączać — wtedy jest ukryty (CSS), a
  // `Layout.scss` zdejmuje zarezerwowany pod nim odstęp. Zostaje w DOM, bo strona może wstawić własne
  // pozycje przez slot — wtedy pasek wraca.
  const isEmpty = items.length + (hasChat ? 1 : 0) < 2;

  return (
    <nav className={`mobile-tab-bar${isEmpty ? ' mobile-tab-bar--empty' : ''}`}>
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            `mobile-tab-bar__item${
              (item.match ? `${location.pathname}/`.startsWith(`${item.match}/`) : isActive)
                ? ' mobile-tab-bar__item--active'
                : ''
            }`
          }
        >
          <span className={`${item.icon} ico--sm mobile-tab-bar__icon`} />
          <span className="mobile-tab-bar__label">{item.label}</span>
        </NavLink>
      ))}
      {hasChat && (
        <button type="button" className="mobile-tab-bar__item" onClick={() => showIntercom()}>
          <span className="ico--chat ico--sm mobile-tab-bar__icon" />
          <span className="mobile-tab-bar__label">{t('mobileNavHelp')}</span>
        </button>
      )}
      <span id={MOBILE_TAB_BAR_PAGE_ID} className="mobile-tab-bar__page" />
    </nav>
  );
};

export default MobileTabBar;
